import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

interface ImageDisplayProps {
  imageData?: string;
  imageUrl?: string;
  promptText?: string;
  modelName?: string;
  fileFormat?: string;
  isLoading: boolean;
}

export function ImageDisplay({ 
  imageData, 
  imageUrl, 
  promptText, 
  modelName, 
  fileFormat = "webp", 
  isLoading 
}: ImageDisplayProps) {
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [fileExtension, setFileExtension] = useState(fileFormat || "webp");

  useEffect(() => {
    // 清理之前的URL，避免内存泄漏
    if (downloadUrl && !imageUrl) {
      URL.revokeObjectURL(downloadUrl);
    }

    // 设置文件扩展名
    setFileExtension(fileFormat || "webp");

    // 处理Base64图像数据
    if (imageData) {
      try {
        // 创建下载URL
        const byteCharacters = atob(imageData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: `image/${fileExtension}` });
        const url = URL.createObjectURL(blob);
        setDownloadUrl(url);
      } catch (error) {
        console.error("创建下载URL时出错:", error);
      }
    }

    // 清理函数
    return () => {
      if (downloadUrl && !imageUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [imageData, imageUrl, fileFormat]);

  const handleDownload = () => {
    // 如果有URL，直接从URL下载
    if (imageUrl) {
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = `ai-generated-${Date.now()}.${fileExtension}`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // 否则使用Base64转换的URL
    if (downloadUrl) {
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `ai-generated-${Date.now()}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // 确定图像源
  const imageSrc = imageUrl || (imageData ? `data:image/${fileExtension};base64,${imageData}` : null);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>生成的图像</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center space-y-4">
        {isLoading ? (
          <div className="w-full aspect-square flex items-center justify-center bg-muted/30 rounded-md">
            <div className="text-center">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-muted-foreground">正在生成图像...</p>
            </div>
          </div>
        ) : imageSrc ? (
          <>
            <div className="relative w-full">
              <img
                src={imageSrc}
                alt="生成的AI图像"
                className="w-full rounded-md shadow-md"
              />
            </div>
            <div className="w-full space-y-4">
              <Separator />
              <div className="space-y-2 text-sm">
                {modelName && (
                  <div>
                    <span className="font-semibold">模型:</span> {modelName}
                  </div>
                )}
                {fileFormat && (
                  <div>
                    <span className="font-semibold">格式:</span> {fileFormat.toUpperCase()}
                  </div>
                )}
                {promptText && (
                  <div>
                    <span className="font-semibold">提示词:</span> {promptText}
                  </div>
                )}
              </div>
              <Button onClick={handleDownload} className="w-full">
                下载图像
              </Button>
            </div>
          </>
        ) : (
          <div className="w-full aspect-square flex items-center justify-center bg-muted/30 rounded-md">
            <p className="text-muted-foreground text-center">
              配置参数并生成图像以在此处查看结果。
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
} 